import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline'
import Button from '@/components/misc/Button'
import { ButtonIcon } from '@/components/new/buttons/ButtonIcon'
import { Button as ButtonCancelar } from '@/components/ui/button'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useModalWizardStepsContext } from '@/components/new/modalWizardSteps/ModalWizardStepsContext'
import { InputForm } from '@/components/new/form/InputForm'
import ButtonLoading from '@/components/misc/Button'
import {
  ButtonPrimary,
  DataTable,
  Modal,
  SelectForm,
  TableContextProvider,
  TextAreaForm,
} from '@/components/new'
import { columns } from './columns'
import { ICost } from '@/containers/logistic/expedients/wizzardExpedient/tabs/cost/interfaces'
import { useForm } from 'react-hook-form'
import { Form } from '@/components/ui/form'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { InputCurrencyForm } from '@/components/new/form/InputCurrencyForm'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getCurrencies } from '@/api/currenciesAPI'
import { getCostCategories } from '@/api/costCategoriesAPI'
import { useAppSelector } from '@/store/hooks'
import {
  deleteContainerCosts,
  getContainerCosts,
  patchContainerCosts,
  postContainerCosts,
  getContainerProducts,
  postContainerProducts,
  patchContainerProducts,
  deleteContainerProducts,
} from '@/api/containersAPI'
import { toast } from 'react-toastify'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import SpinnerLoading from '@/components/misc/SpinnerLoading'
import { IContainer, IProduct } from '../../../interfaces/interfaces'
import useContainerWizzardSteps from '@/state/useContainerWizzardSteps'
import { SelectFormProducts } from './SelectFormProducts'
import { getProducts, postProducts } from '@/api/productsAPI'
import { IProd } from '@/containers/logistic/expedients/wizzardExpedient/tabs/cargo/CargoExpTab'
import { LightningBoltOutline } from 'heroicons-react'
import { productTypes } from '@/utils/staticData'
import { getProviders } from '@/api/providersAPI'
import { SelectFormProviders } from '../../../components/SelectFormProviders'

export default function ProductsContainerTab({
  isEditing,
  editContainer,
}: {
  isEditing?: boolean
  editContainer?: IContainer
}) {
  const context = isEditing
    ? useModalWizardTabsContext()
    : useModalWizardStepsContext()
  const { useFormWizzard } = context
  const { control, getValues, setValue } = useFormWizzard
  const [showProductContainerModal, setShowProductContainerModal] =
    useState(false)
  const [showNewProductModal, setShowNewProductModal] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [costTable, setCostTable] = useState<ICost[]>([])
  const [isCostEditing, setIsCostEditing] = useState(false)
  const [isProductEditing, setIsProductEditing] = useState(false)
  const [showDeleteProduct, setShowDeleteProduct] = useState(false)
  const [productToDelete, setProductToDelete] = useState<number>()
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null)
  const [dataTableNewContainer, setDataTableNewContainer] = useState<IProd[]>(
    []
  )
  const data = useContainerWizzardSteps((state) => state.data)
  const updateData = useContainerWizzardSteps((state) => state.updateData)
  // 'products' no existe en DataType, así que inicializamos como array vacío si no existe
  const [productTable, setProductTable] = useState<IProduct[]>([])
  const [showEmptyModal, setShowEmptyModal] = useState(false)
  const [showRepetedModal, setShowRepetedModal] = useState(false)
  const [productSelected, setProductSelected] = useState<string | undefined>(
    undefined
  )
  const [providerSelected, setProviderSelected] = useState<import('@/api/filesAPI').CompanyType | undefined>(undefined)

  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: () =>
      getProviders({
        page: 1,
        per_page: 10,
        search: '',
      }),
    staleTime: 5 * 60 * 1000,
  })

  const { data: containerProducts, isLoading: isProductLoading } = useQuery({
    queryKey: ['container_products'],
    queryFn: () =>
      getProducts({
        page: 1,
        per_page: 10,
        search: '',
        type: 'STOCK,RAW,MANUFACTURED,INPUT,ASSET,VARIATION',
      }),
    staleTime: 5 * 60 * 1000,
  })
  

  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
  })
  const { data: costCategories } = useQuery({
    queryKey: ['costCategories'],
    queryFn: getCostCategories,
  })
  const queryClient = useQueryClient()

  const { data: containerCosts, isLoading } = useQuery({
    queryKey: ['container_costs', editContainer?.id],
    queryFn: () => getContainerCosts({ id: editContainer?.id as number }),
    enabled: Boolean(editContainer?.id),
    staleTime: 5 * 60 * 1000,
  })

  const postNewProduct = useMutation({
    mutationFn: postProducts,
    onSuccess: () => {
      setIsSending(false)
      setShowNewProductModal(false)
      setShowProductContainerModal(true)
      toast.success('El producto se ha creado con éxito')
      queryClient.invalidateQueries({ queryKey: ['file_costs'] })
      form.reset()
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const postContainerCostsMutation = useMutation({
    mutationFn: postContainerCosts,
    onSuccess: () => {
      setIsSending(false)
      setShowProductContainerModal(false)
      toast.success('El costo del contenedor se ha creado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_costs'] })

      form.reset()
      setSelectedCategory(null)
      setIsCostEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const updateContainerCostsMutation = useMutation({
    mutationFn: patchContainerCosts,
    onSuccess: () => {
      setIsSending(false)
      setShowProductContainerModal(false)
      toast.success('El costo del contenedor se ha actualizado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_costs'] })

      form.reset()
      setSelectedCategory(null)
      setIsCostEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const deleteContainerCostsMutation = useMutation({
    mutationFn: deleteContainerCosts,
    onSuccess: () => {
      setIsSending(false)
      setShowDeleteProduct(false)
      toast.success('El costo del contenedor se ha eliminado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_costs'] })

      form.reset()
      setSelectedCategory(null)
      setIsCostEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  // Container products mutations
  const postContainerProductsMutation = useMutation({
    mutationFn: postContainerProducts,
    onSuccess: () => {
      setIsSending(false)
      setShowProductContainerModal(false)
      toast.success('El producto del contenedor se ha creado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_products'] })

      form.reset()
      setIsProductEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const updateContainerProductsMutation = useMutation({
    mutationFn: patchContainerProducts,
    onSuccess: () => {
      setIsSending(false)
      setShowProductContainerModal(false)
      toast.success('El producto del contenedor se ha actualizado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_products'] })

      form.reset()
      setIsProductEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const deleteContainerProductsMutation = useMutation({
    mutationFn: deleteContainerProducts,
    onSuccess: () => {
      setIsSending(false)
      setShowDeleteProduct(false)
      toast.success('El producto del contenedor se ha eliminado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_products'] })

      form.reset()
      setIsProductEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  useEffect(() => {
    setValue('products', '')
  }, [])

  useEffect(() => {
    if (!isEditing) {
      const initialData = data.listCosts.map((item, idx) => ({
        id: idx, // No hay id en listCosts, usamos el índice
        cost: {
          amount: item.costAmount,
          codeCurrency: item.costCodecurrency?.toUpperCase() ?? '-',
        },
        category: '-', // No hay categoría en listCosts
        observations: '-', // No hay observaciones en listCosts
        actions: {
          remove: true,
          edit: true,
        },
      }))

      setCostTable((prev) => [...prev, ...(initialData as ICost[])])
    }
  }, [])

  useEffect(() => {
    if (isEditing) {
      if (!isLoading) {
        const initialData = (containerCosts?.fixedCosts ?? []).map((item) => ({
          id: item.id,
          cost: {
            amount: item?.amountPrice ?? '-',
            codeCurrency: item?.codeCurrency?.toUpperCase() ?? '-',
          },
          category: costCategories?.find(
            (elem) => elem?.id === item?.fixedCostCategory?.id
          )?.name,
          observations: item?.observations ?? '-',
          actions: {
            remove: true,
            edit: true,
          },
        }))
        setCostTable(initialData as ICost[])
      }
    }
  }, [containerCosts, isLoading])

  const { business } = useAppSelector((state) => state.init)

  const form = useForm()
  const { register } = form
  
  const saveNewProduct = (data: any) => {
    setIsSending(true)
    //setValue('products', [data.product_name, undefined])
    setProductSelected(data.product_name)
    postNewProduct.mutate({
      name: data.product_name,
      type: data.product_type,
      prices: [
        {
          price: data.product_price?.amount ?? 0,
          codeCurrency: data.product_price?.codeCurrency ?? '',
        },
      ],
    })
  }

  const saveProductContainer = (product: any) => {
    const productData = {
      productId: product?.product?.id,
      noPackages: Number(product?.product_noPackages),
      description: product?.description,
      uniqueCode: product?.product_uniqueCode,
      provider: product?.product_provider,
      cost: product?.product_cost,
      availableQuantity: product?.product_quantity,
      expirationAt: product?.product_expirationAt,
    }
    const id = product?.id

    if (isEditing) {
      if (isProductEditing) {
        setIsSending(true)
        updateContainerProductsMutation.mutate({
          id,
          params: productData,
        })
      } else {
        setIsSending(true)
        postContainerProductsMutation.mutate({
          id: editContainer?.id as number,
          params: productData,
        })
      }
    } else {
      isProductEditing
        ? setProductTable((prev) => prev.map((item) => {
            if (item.id === id) {
              return {
                ...item,
                noPackages: Number(product.product_noPackages),
                description: product.description,
                product: item.product,
                uniqueCode: product.product_uniqueCode,
                product_provider: product.product_provider,
                product_cost: product.product_cost,
                availableQuantity: product.product_quantity,
                expirationAt: product.product_expirationAt,
              }
            } else {
              return item
            }
          }))
        : setProductTable((prev) => [
            ...prev,
            {
              id: prev.length,
              noPackages: Number(product.product_noPackages),
              description: product.description,
              product: {
                id: getValues('products')[1],
                name: productSelected ?? getValues('products')[0],
              },
              uniqueCode: product.product_uniqueCode,
              product_provider: product.product_provider,
              product_cost: product.product_cost,
              availableQuantity: product.product_quantity,
              expirationAt: product.product_expirationAt,
              actions: {
                remove: true,
                edit: true,
              },
            },
          ])
      // El manejo de productos debe hacerse en el estado local 'productTable'.
      // Si se requiere sincronizar con el store global, implementar aquí la lógica adecuada.
    }
  }

  return (
    <div className={'h-[60dvh]'}>
      {(isLoading || isProductLoading) ? (
        <div className="flex h-full w-full items-center justify-center">
        <SpinnerLoading />
        </div>
      ) : (
        <>
          <div className={'mb-2 flex'}>
            <SelectFormProducts
              label={'Listado de productos'}
              name={'products'}
              data={Array.isArray(containerProducts) ? containerProducts.map(
                ({ id, name, salesCode, description }) => ({
                  id: id,
                  title: salesCode ? `${name}-${salesCode}` : name,
                  subtitle: description,
                })
              ) : []}
              control={control}
            />
            <div className={'ms-5 flex items-end space-x-2'}>
              <ButtonIcon
                onClick={() => {
                  if (getValues('products')) {
                    if (
                      productTable.find(
                        (item) =>
                          item.product.name ===
                            getValues('products')[0].split('-')[0] ||
                          item.product.name === getValues('products')[0]
                      )
                    ) {
                      setShowRepetedModal(true)
                    } else {
                      setShowProductContainerModal(true)
                    }
                  } else {
                    setShowEmptyModal(true)
                  }
                }}
              >
                <PlusIcon className="h-5" color={'white'} strokeWidth={3} />
              </ButtonIcon>
              <ButtonIcon onClick={() => setShowNewProductModal(true)}>
                <LightningBoltOutline
                  className="h-5"
                  color={'white'}
                  strokeWidth={2}
                  onPointerEnterCapture={() => {}}
                  onPointerLeaveCapture={() => {}}
                />
              </ButtonIcon>
              {/*<ButtonIcon onClick={() => {}}>
                <DownloadOutline
                  className="h-5"
                  color={'white'}
                  strokeWidth={3}
                />
              </ButtonIcon> */}
            </div>
          </div>
          <TableContextProvider data={productTable} isLoading={false}>
            <DataTable
              columns={[
                ...columns,
                {
                  accessorKey: 'actions',
                  header: 'Acciones',
                  cell: ({ row }) => {
                    return (
                      <div className={'flex justify-center'}>
                        {row.original.actions.edit && (
                          <div
                            role={'button'}
                            className={`p-2`}
                            onClick={() => {
                              setShowProductContainerModal(true)
                              setIsProductEditing(true)
                              form.setValue('id', row.original.id)
                              form.setValue(
                                'product_quantity',
                                row.original.availableQuantity
                              )
                              setProductSelected(row.original.product.name)
                              form.setValue(
                                'product_quantity',
                                row.original.availableQuantity
                              )
                              form.setValue(
                                'product_noPackages',
                                row.original.noPackages
                              )
                              form.setValue(
                                'description',
                                row.original.description
                              )
                              form.setValue(
                                'product_uniqueCode',
                                row.original.uniqueCode
                              )
                              form.setValue(
                                'product_provider',
                                row.original.product_provider
                              )
                              form.setValue(
                                'product_cost',
                                row.original.product_cost
                              )
                              form.setValue(
                                'product_expirationAt',
                                row.original.expirationAt
                              )
                              setProviderSelected(row.original.product_provider)
                            }}
                          >
                            <PencilSquareIcon
                              className={`h-5 w-5 text-blue-700`}
                            />
                          </div>
                        )}
                        <div
                          role={'button'}
                          className={`p-2`}
                          onClick={() => {
                            setShowDeleteProduct(true)
                            setProductToDelete(row.original.id)
                          }}
                        >
                          <TrashIcon className={`h-5 w-5 text-red-700`} />
                        </div>
                      </div>
                    )
                  },
                },
              ]}
              storageKey={'ProductsContainerTab'}
            />
          </TableContextProvider>
        </>
      )}
      {showProductContainerModal && (
        <Modal
          isOpen={showProductContainerModal}
          onClose={() => {
            setIsCostEditing(false)
            setShowProductContainerModal(false)
            setSelectedCategory(null)
            form.reset()
          }}
          title={
            productSelected ??
            (getValues('products') && getValues('products')[0])
          }
          size={'xs'}
          showDialogAlert={false}
        >
          <div
            className={
              'flex w-full flex-col overflow-y-scroll px-2 scrollbar-thin'
            }
          >
            <Form {...form}>
              <div className={'flex w-full flex-col space-y-2'}>
                <div className="flex w-full gap-2">
                  <div className="flex-1">
                    <InputForm
                      name={'product_quantity'}
                      label={'Cantidad'}
                      control={form.control}
                      placeholder={'Cantidad'}
                      type="number"
                      rules={{
                        required: 'Campo requerido',
                      }}
                    />
                  </div>
                </div>
                <div className="flex w-full gap-2">
                  <div className="flex-1" style={{ marginTop: '.14rem' }}>
                    <label className="mb-2.5 block text-sm font-medium text-gray-900">
                      Fecha de expiración
                    </label>
                    <input
                      id="product_expirationAt"
                      type="date"
                      {...register('product_expirationAt', {
                        required: {
                          value: true,
                          message: 'El nombre es obligatorio',
                        },
                      })}
                      //value={formData.shouldArriveAt}
                      /*onChange={(e) =>
                        handleChange('shouldArriveAt', e.target.value)
                      }*/
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <InputForm
                      name={'product_noPackages'}
                      label={'Número de paquetes'}
                      control={form.control}
                      placeholder={'Número de paquetes'}
                      type="number"
                    />
                  </div>
                </div>
                <div className="flex w-full gap-2">
                  <div className="flex-1">
                    {currencies && (
                      <InputCurrencyForm
                        currenciesData={Array.isArray(currencies) ? currencies.map(({ code }) => ({
                          code: code,
                          displayName: code,
                        })) : []}
                        name={'product_cost'}
                        control={form.control}
                        label={'Costo (*)'}
                        rules={{
                          required: 'Campo requerido',
                        }}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <SelectFormProviders
                      name={'product_provider'}
                      data={Array.isArray(providers) ? providers.map(({ id, name }) => ({
                        id,
                        title: name,
                        subtitle: '',
                      })) : []}
                      control={form.control}
                      label={'Proveedor (*)'}
                      rules={{
                        required: 'Campo requerido',
                      }}
                      defaultValue={providerSelected}
                    />
                  </div>
                </div>
                <TextAreaForm
                  name={'description'}
                  control={form.control}
                  label={'Observaciones'}
                />
              </div>
              <div className={'mt-10 flex w-full justify-between'}>
                <ButtonCancelar
                  disabled={isSending}
                  variant="outline"
                  onClick={() => {
                    setIsCostEditing(false)
                    setShowProductContainerModal(false)
                    setSelectedCategory(null)
                    form.reset()
                  }}
                >
                  Cancelar
                </ButtonCancelar>
                {isSending ? (
                  <ButtonLoading
                    loading={isSending}
                    name="Guardando..."
                  ></ButtonLoading>
                ) : (
                  <ButtonPrimary
                    onClick={form.handleSubmit(saveProductContainer)}
                    text={'Guardar'}
                  />
                )}
              </div>
            </Form>
          </div>
        </Modal>
      )}
      {showNewProductModal && (
        <Modal
          isOpen={showNewProductModal}
          onClose={() => {
            setIsCostEditing(false)
            setShowNewProductModal(false)
            setSelectedCategory(null)
            form.reset()
          }}
          title={'Nuevo Producto'}
          size={'xs'}
          showDialogAlert={false}
        >
          <div className={'flex w-full flex-col'}>
            <Form {...form}>
              <div className={'flex w-full flex-col space-y-2'}>
                <SelectForm
                  name={'product_type'}
                  data={Array.isArray(productTypes) ? productTypes.map(({ id, name }) => ({
                    id,
                    title: name,
                  })) : []}
                  control={form.control}
                  label={'Tipo (*)'}
                  rules={{
                    required: 'Campo requerido',
                  }}
                  //defaultValue={selectedCategory}
                />
                <InputForm
                  name={'product_name'}
                  label={'Nombre'}
                  control={form.control}
                  placeholder={'Nombre'}
                  rules={{
                    required: 'Campo requerido',
                  }}
                />
                {form.watch('product_type') === 'STOCK' && currencies && (
                  <InputCurrencyForm
                    currenciesData={Array.isArray(currencies) ? currencies.map(({ code }) => ({
                      code: code,
                      displayName: code,
                    })) : []}
                    name={'product_price'}
                    control={form.control}
                    label={'Precio de venta (*)'}
                    rules={{
                      required: 'Campo requerido',
                    }}
                  />
                )}
              </div>
              <div className={'mt-10 flex w-full justify-between'}>
                <ButtonCancelar
                  disabled={isSending}
                  variant="outline"
                  onClick={() => {
                    setIsCostEditing(false)
                    setShowNewProductModal(false)
                    setSelectedCategory(null)
                    form.reset()
                  }}
                >
                  Cancelar
                </ButtonCancelar>
                {isSending ? (
                  <ButtonLoading
                    loading={isSending}
                    name="Guardando..."
                  ></ButtonLoading>
                ) : (
                  <ButtonPrimary
                    onClick={form.handleSubmit(saveNewProduct)}
                    text={'Guardar'}
                  />
                )}
              </div>
            </Form>
          </div>
        </Modal>
      )}
      <AlertDialog open={showDeleteProduct}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el producto?</AlertDialogTitle>
            <AlertDialogDescription>
              Si elimina ahora, perderá todos los datos asociados a este
              producto. ¿Está seguro de que desea eliminar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteProduct(false)}>
              Cancelar
            </AlertDialogCancel>
            <Button
              name={'Sí, eliminar'}
              loading={isSending}
              disabled={isSending}
              action={() => {
                if (isEditing) {
                  //setIsSending(true)
                  //deleteExpedientCosts.mutate(productToDelete as number)
                } else {
                  setProductTable(
                    productTable.filter(
                      (item) => item.id !== (productToDelete as number)
                    )
                  )
                  // El manejo de productos debe hacerse en el estado local 'productTable'.
                  setShowDeleteProduct(false)
                  toast.success('El producto se ha eliminado con éxito')
                }
              }}
            />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={showEmptyModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Desea agregar un producto al contenedor?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Debe seleccionar antes el producto deseado para proceder con su
              registro en el contenedor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              name={'Aceptar'}
              action={() => {
                setShowEmptyModal(false)
              }}
            />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={showRepetedModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Desea agregar otro producto al contenedor?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Debe seleccionar antes un producto que no esté dentro del
              contenedor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              name={'Aceptar'}
              action={() => {
                setShowRepetedModal(false)
              }}
            />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
